import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Billing, RequestError, Service } from 'webapp-types';
import { BillingAddonType, BillingCycle } from 'webapp/enums/enums';

import eq from 'webapp/helpers/eq';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import SubmitButtonComponent from 'webapp/components/submit-button';
import ToolTipComponent from 'webapp/components/tool-tip';

import type { TrackingOriginTypeForTrialPopupOpened } from 'webapp/services/billing';

interface Args {
	item: Billing.Addon;
	billingCycle: BillingCycle;
	trackingOrigin?: string;
}

export default class BillingAddonComponent extends Component<Args> {
	@service('billing') declare billingService: Service.Billing;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('request') declare requestService: Service.Request;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('flow/promotions') declare flowPromotionsService: Service.Flow.Promotions;

	@tracked submitInProgress = false;

	get canAddonBeEnabled(): boolean {
		return !this.billingService.activePlan?.isFree;
	}

	get addText(): string {
		let text = 'Enable';
		switch (this.args.item.id) {
			case BillingAddonType.AI_CREDITS:
				text = 'Manage credits';
				break;
			case BillingAddonType.CLIENT_PACK:
				text = 'Manage packs';
				break;
			case BillingAddonType.PRIORITY_SYNC:
				text = 'Setup';
				break;
		}
		return text;
	}

	get addTextForTrial(): string {
		let text = 'Add to subscription';
		switch (this.args.item.id) {
			case BillingAddonType.AI_CREDITS:
			case BillingAddonType.CLIENT_PACK:
			case BillingAddonType.PRIORITY_SYNC:
				text = this.addText;
				break;
		}
		return text;
	}

	get showEnabledOnTrialWarning(): boolean {
		const model = this.args.item;
		return (
			this.billingService.isTrial &&
			model.isEnabled &&
			![
				BillingAddonType.PRIORITY_SYNC,
				BillingAddonType.AI_CREDITS,
				BillingAddonType.CLIENT_PACK,
			].includes(model.id)
		);
	}

	get showEnabledActions(): boolean {
		const model = this.args.item;
		return !this.billingService.isAddonCountable(model.id) && model.isEnabled;
	}

	@action
	manageAddon() {
		const model = this.args.item;
		if (model.isTrialing) {
			this.disableTrialAddon();
		} else if (model.isCanceled) {
			this.enableAddon();
		} else {
			this.disableAddon();
		}
	}

	@action
	disableAddon(): void {
		const model = this.args.item;
		if (!model.isAutoEnabled) {
			if (this.billingService.isTrial) {
				this.submitInProgress = true;

				this.billingService
					.removeAddon(model.id)
					.then(
						() => {
							this.infolayerService.ok('Addon removed!');
						},
						(errorData: RequestError) => {
							this.requestService.handleGenericThrownError(errorData);
						},
					)
					.finally(() => {
						this.submitInProgress = false;
					});
			} else {
				this.billingService.openAddonCancelPopup(model);
			}
		}
	}

	@action
	enableAddon() {
		const model = this.args.item;
		// overview passes 'billing_page', the plans page passes 'pricing_page' —
		// both are valid TrackingOriginTypeForTrialPopupOpened values
		const origin = this.args.trackingOrigin as
			TrackingOriginTypeForTrialPopupOpened | undefined;
		if (model.id === BillingAddonType.AI_CREDITS) {
			this.billingService.openAiCreditsPurchasePopup(origin);
		} else if (model.id === BillingAddonType.CLIENT_PACK) {
			this.billingService.openClientPacksPurchasePopup(origin);
		} else if (model.id === BillingAddonType.PRIORITY_SYNC) {
			this.mmSourcesService.openSyncIntervalModal(undefined, this.args.trackingOrigin);
		} else {
			if (this.billingService.isTrial) {
				this.submitInProgress = true;

				this.billingService
					.addAddon(model.id)
					.then(
						() => {
							this.infolayerService.ok('Addon added!');
						},
						(errorData: RequestError) => {
							this.requestService.handleGenericThrownError(errorData);
						},
					)
					.finally(() => {
						this.submitInProgress = false;
					});
			} else {
				this.billingService.openAddonPurchasePopup(model);
			}
		}
	}

	@action
	disableTrialAddon(): void {
		const promotion = this.flowPromotionsService.getActiveTrialAddonPromotion(
			this.args.item.id,
		);

		if (promotion) {
			this.submitInProgress = true;

			void this.flowPromotionsService
				.updatePromotion({ promotionId: promotion.id, commitment: false })
				.finally(() => {
					this.submitInProgress = false;
				});
		}
	}

	<template>
		<li class="addon {{if @item.isEnabled 'enabled'}}">
			<div class="content-ct">
				<div class="header-ct">
					<div class="title">
						<CommonUiIconComponent @name={{@item.icon}} @size="lg" />
						<h3>{{@item.name}}</h3>
					</div>
					{{#if @item.isTrialing}}
						<span class="addon-trialing">
							Your free trial expires in
							{{@item.timeleftText}}
						</span>
					{{/if}}
				</div>
				<div class="text-ct">
					<p>
						{{@item.description}}
						{{#if @item.learnMoreUrl}}
							<a href={{@item.learnMoreUrl}} target="_blank">
								Learn more
							</a>
						{{/if}}
					</p>
				</div>
				{{#if this.showEnabledOnTrialWarning}}
					<div class="action-with-info">
						<span>Add on will be enabled on the active paid subscription</span>
					</div>
				{{/if}}
			</div>
			<div class="action-ct">
				{{#if @item.prices.current.price}}
					{{#if @item.isAutoEnabled}}
						<div class="price">
							<strong>$0</strong>/mo
						</div>
					{{else}}
						<div class="price">
							<strong>
								{{@item.prices.currentMonthly.priceFormatted}}
							</strong>
							/mo
						</div>
						{{#if (eq @item.prices.current.cycleText "mo")}}
							{{! Without text - used because unless not recommended with helper eq }}
						{{else}}
							<div class="billing-cycle-total">
								Billed
								{{@billingCycle}}:
								{{@item.prices.current.priceFormatted}}
							</div>
						{{/if}}
					{{/if}}
				{{/if}}

				{{#if this.showEnabledActions}}
					{{#if this.billingService.isTrial}}
						<SubmitButtonComponent
							@text="Enabled"
							@noDelay={{true}}
							@onTrigger={{this.disableAddon}}
							@theme="blue-outlined"
							@submitInProgress={{this.submitInProgress}}
							@isDisabled={{@item.isDisabled}}
							@tooltipText={{@item.tooltipText}}
							@tooltipTheme="light"
						/>
					{{else}}
						{{#if this.billingService.isPaused}}
							<a role="button" class="button theme-grey-outlined disabled">
								<CommonUiIconComponent @name="Check" @size="sm" />
								Enabled
								<ToolTipComponent @text={{@item.tooltipText}} @theme="light" />
							</a>
						{{else}}
							<a
								role="button"
								class="button theme-blue-outlined
									{{if @item.isAutoEnabled 'auto-enabled'}}"
								{{on "click" this.manageAddon}}
							>
								{{#if @item.isTrialing}}
									<span class="enabled-state">
										<CommonUiIconComponent @name="Hourglass" @size="sm" />
										Trialing
									</span>
								{{else}}
									<span class="enabled-state">
										<CommonUiIconComponent @name="Check" @size="sm" />
										Enabled
									</span>
								{{/if}}
								{{#if @item.isCanceled}}
									<span class="reenable">Re-enable</span>
								{{else}}
									<span class="disable">Disable</span>
								{{/if}}

								{{#if @item.isAutoEnabled}}
									{{#if @item.inactiveInTrial}}
										{{#if this.billingService.isTrial}}
											<ToolTipComponent
												@text="Addon enabled free of charge for current plan, however it can be used once you are on the paid subscription."
												@theme="light"
											/>
										{{else}}
											<ToolTipComponent
												@text="Addon enabled free of charge for current plan."
												@theme="light"
											/>
										{{/if}}
									{{else}}
										<ToolTipComponent
											@text="Addon enabled free of charge for current plan."
											@theme="light"
										/>
									{{/if}}
								{{/if}}
							</a>
						{{/if}}
					{{/if}}
				{{else}}
					{{#if @item.availableForActivePlan}}
						{{#if this.canAddonBeEnabled}}
							{{#if this.billingService.isTrial}}
								{{#if @item.canAddOnTrial}}
									<SubmitButtonComponent
										@text={{this.addTextForTrial}}
										@noDelay={{true}}
										@onTrigger={{this.enableAddon}}
										@submitInProgress={{this.submitInProgress}}
										@isDisabled={{@item.isDisabled}}
										@tooltipText={{@item.tooltipText}}
										@tooltipTheme="light"
									/>
								{{else}}
									<a class="button theme-grey disabled">
										{{this.addText}}
										<ToolTipComponent
											@text="Add-ons can be enabled for paid subscriptions only."
											@theme="light"
										/>
									</a>
								{{/if}}
							{{else}}
								<SubmitButtonComponent
									@text={{this.addText}}
									@noDelay={{true}}
									@onTrigger={{this.enableAddon}}
									@submitInProgress={{this.submitInProgress}}
									@isDisabled={{@item.isDisabled}}
									@tooltipText={{@item.tooltipText}}
									@tooltipTheme="light"
								/>
							{{/if}}
						{{else}}
							<a class="button theme-grey disabled">
								{{this.addText}}
								<ToolTipComponent
									@text="Add-ons can be enabled for paid subscriptions only."
								/>
							</a>
						{{/if}}
					{{else}}
						<a class="button theme-grey disabled">
							{{this.addText}}
							<ToolTipComponent
								@text="Add-on not available on current plan."
								@theme="light"
							/>
						</a>
					{{/if}}
				{{/if}}
			</div>
		</li>
	</template>
}
